import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowRightLeft, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { getMigrations } from '../api/migrations';
import MigrateToolModal from '../components/forms/MigrateToolModal';

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const Migrations = () => {
  const [migrations, setMigrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [migrateTarget, setMigrateTarget] = useState(null);
  const limit = 15;

  const fetchMigrations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMigrations({ page, limit });
      setMigrations(res.data.data);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load migrations.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchMigrations(); }, [fetchMigrations]);

  const totalPages = Math.ceil(total / limit);

  const filtered = migrations.filter((m) =>
    !search ||
    m.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
    m.employee_email?.toLowerCase().includes(search.toLowerCase()) ||
    m.from_tool_name?.toLowerCase().includes(search.toLowerCase()) ||
    m.to_tool_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tool Migrations</h1>
          <p className="text-sm text-gray-500 mt-1">Full history of all AI tool migrations.</p>
        </div>
      </div>

      {/* Search */}
      <div className="card p-4 mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="input pl-9"
            placeholder="Search by employee or tool name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 font-medium text-gray-500">#</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">Employee</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">Department</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">From Tool</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500"></th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">To Tool</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">New Period</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">Migrated On</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <svg className="animate-spin h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <ArrowRightLeft className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-500">No migrations yet</p>
                        <p className="text-xs mt-0.5">
                          {search ? `No results for "${search}"` : 'Migrations will appear here once performed from the Employees page.'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((m, idx) => (
                  <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 text-gray-400">{(page - 1) * limit + idx + 1}</td>
                    <td className="px-5 py-4">
                      <p className="font-medium text-gray-900">{m.employee_name}</p>
                      <p className="text-xs text-gray-400">{m.employee_email}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                        {m.department}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      {m.from_tool_name ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
                          {m.from_tool_name}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <ArrowRightLeft className="w-4 h-4 text-gray-300" />
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                        {m.to_tool_name}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">
                      {fmt(m.new_start_date)} → {fmt(m.new_end_date)}
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">
                      {fmt(m.migration_date)}
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-400 max-w-[160px] truncate">
                      {m.notes || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && total > limit && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-sm">
            <p className="text-gray-500">
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-gray-600 px-1">{page} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {!loading && filtered.length > 0 && total <= limit && (
          <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400">
            {total} migration{total !== 1 ? 's' : ''} total
          </div>
        )}
      </div>

      <MigrateToolModal
        isOpen={!!migrateTarget}
        onClose={() => setMigrateTarget(null)}
        onSaved={fetchMigrations}
        employee={migrateTarget}
      />
    </div>
  );
};

export default Migrations;
